import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getTenantBySlug } from '../services/tenantService';
import { register } from '../services/authService';
import { submitClaim } from '../services/membershipService';
import { useAuth } from '../context/AuthContext';

/**
 * TenantJoinPage — per-tenant branded join/signup page.
 *
 * Route: /t/:tenantSlug/join  (public — no auth required)
 *
 * Behaviour:
 *  - Not logged in → shows account creation form; on success registers user
 *    then submits a membership claim for this tenant. User is redirected to login.
 *  - Logged in → shows a one-click membership claim form if they haven't claimed yet.
 *
 * Branding: tenant name, logo, primaryColor from Tenant.branding are applied inline.
 */
const TenantJoinPage = () => {
  const { tenantSlug } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const [tenant, setTenant]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Registration form state (unauthenticated path)
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');

  // Claim form state (authenticated path)
  const [claimNote, setClaimNote] = useState('');

  const [submitting, setSub]  = useState(false);
  const [msg, setMsg]         = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getTenantBySlug(tenantSlug)
      .then((res) => setTenant(res.data))
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  // ── Unauthenticated: register account + auto-submit membership claim ───────
  const handleRegisterAndJoin = async (e) => {
    e.preventDefault();
    setMsg('');
    if (password.length < 8) { setMsg('Password must be at least 8 characters.'); return; }
    if (password !== confirm)  { setMsg('Passwords do not match.');               return; }

    setSub(true);
    try {
      // 1. Create account
      await register({ firstName, lastName, email, password, tenantId: tenant._id });
      // 2. The backend register endpoint should auto-submit a claim too — but if
      //    not, we redirect to login with a success message. User can claim after login.
      setSuccess(
        `Account created! Please check ${email} to verify, then log in and complete your membership application.`
      );
    } catch (err) {
      setMsg(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSub(false);
    }
  };

  // ── Authenticated: submit membership claim ────────────────────────────────
  const handleClaim = async (e) => {
    e.preventDefault();
    setMsg('');
    setSub(true);
    try {
      await submitClaim({ tenantId: tenant._id, note: claimNote });
      setSuccess(
        `Your membership request for ${tenant.branding?.displayName || tenant.name} has been submitted. The community administrator will review it shortly.`
      );
    } catch (err) {
      setMsg(err.response?.data?.message || 'Could not submit claim. Please try again.');
    } finally {
      setSub(false);
    }
  };

  // ── Render states ─────────────────────────────────────────────────────────
  if (loading) return <div className="page-loading">Loading…</div>;

  if (notFound) {
    return (
      <div className="page join-page">
        <h2>Community Not Found</h2>
        <p>The community <code>{tenantSlug}</code> does not exist or is not accepting members at this time.</p>
        <Link to="/">Return to Home</Link>
      </div>
    );
  }

  const displayName   = tenant?.branding?.displayName || tenant?.name;
  const primaryColor  = tenant?.branding?.primaryColor;
  const logoUrl       = tenant?.branding?.logoUrl;
  const bannerText    = tenant?.branding?.bannerText;

  const brandStyle = primaryColor ? { '--tenant-color': primaryColor } : {};

  return (
    <div className="page join-page" style={brandStyle}>
      {/* ── Tenant branding header ── */}
      <div className="join-header" style={primaryColor ? { borderTopColor: primaryColor } : {}}>
        {logoUrl && (
          <img src={logoUrl} alt={`${displayName} logo`} className="join-header__logo" />
        )}
        <h1 className="join-header__name">{displayName}</h1>
        {bannerText && <p className="join-header__banner">{bannerText}</p>}
        {tenant?.description && <p className="join-header__desc">{tenant.description}</p>}
      </div>

      {/* ── Success message ── */}
      {success && (
        <div className="join-success" role="status">
          <p>{success}</p>
          {user
            ? <Link to="/dashboard" className="btn">Go to Dashboard</Link>
            : <Link to="/login"     className="btn">Sign In</Link>
          }
        </div>
      )}

      {/* ── Content (hidden after success) ── */}
      {!success && (
        <div className="join-body">
          {user ? (
            // ── Authenticated: claim form ──────────────────────────────────
            <div className="join-claim">
              <h2>Request Membership</h2>
              <p>
                You are signed in as <strong>{user.email}</strong>. Submit a membership request
                to join <strong>{displayName}</strong>. A community administrator will review your
                request.
              </p>
              <form onSubmit={handleClaim}>
                <label>
                  Note to administrator (optional)
                  <textarea
                    value={claimNote}
                    onChange={(e) => setClaimNote(e.target.value)}
                    rows={3}
                    placeholder="Briefly describe why you're requesting membership or your connection to this community."
                  />
                </label>
                {msg && <p className="error-msg" role="alert">{msg}</p>}
                <div className="form-actions">
                  <button type="submit" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Request Membership'}
                  </button>
                  <Link to="/dashboard" className="btn-secondary">Cancel</Link>
                </div>
              </form>
            </div>
          ) : (
            // ── Unauthenticated: register + auto-claim ─────────────────────
            <div className="join-register">
              <h2>Create an Account &amp; Join</h2>
              <p>
                Register below to create a Civic Intelligence Platform account and request
                membership in <strong>{displayName}</strong>.
              </p>
              <p className="join-login-hint">
                Already have an account? <Link to={`/login?next=/t/${tenantSlug}/join`}>Sign in</Link>
              </p>
              <form onSubmit={handleRegisterAndJoin} noValidate>
                <div className="form-row">
                  <label>
                    First Name <span aria-hidden="true">*</span>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Last Name <span aria-hidden="true">*</span>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </label>
                </div>
                <label>
                  Email <span aria-hidden="true">*</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </label>
                <label>
                  Password <span aria-hidden="true">*</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <small>Minimum 8 characters.</small>
                </label>
                <label>
                  Confirm Password <span aria-hidden="true">*</span>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </label>
                {msg && <p className="error-msg" role="alert">{msg}</p>}
                <div className="form-actions">
                  <button type="submit" disabled={submitting}>
                    {submitting ? 'Creating account…' : 'Create Account & Request Membership'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantJoinPage;
